// ScreenshotRedactor — step 3 ("Preview & redact") of the in-product widget's
// screenshot flow. See docs/design_handoff_helpdesk/README.md §5 "Screenshot
// governance" and prototype/screens-widget.jsx `WidgetRedact` for the spec
// this implements.
//
// Contract (pinned — a sibling agent consumes this from FeedbackWidget.tsx):
//   <ScreenshotRedactor file={File} onFlattened={(blob: Blob) => void} onCancel={() => void} />
//
// Non-negotiable per the governance section:
//   - Redaction is DESTRUCTIVE and CLIENT-SIDE. The user drags rectangles over
//     a <canvas> rendering of `file`; committed rectangles are painted opaque
//     in `--redact-fill` directly onto that canvas.
//   - The un-redacted bitmap never leaves memory: this component never
//     uploads, never calls an API, and drops every reference to the original
//     image (the <img> element, its object URL, and the ImageData) once the
//     user confirms. Only the flattened canvas's PNG blob is emitted via
//     `onFlattened`; nothing else is exposed to the caller.
//   - Auto-detection (`--redact-auto` / `--redact-auto-bg` dashed candidates
//     from `data-pii`-marked DOM elements) is OUT OF SCOPE: this component
//     receives a File, not a live DOM, so there is nothing to scan. No
//     candidate regions are fabricated — see the note at the bottom of this
//     file.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import CheckIcon from '@mui/icons-material/Check';
import { HdButton } from '../components';

export interface ScreenshotRedactorProps {
  /** The user-selected image. Never uploaded as-is. */
  file: File;
  /** Called with the FLATTENED png once the user confirms redactions. */
  onFlattened: (flattened: Blob) => void;
  /** User backed out — the caller discards the capture. */
  onCancel: () => void;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Canvas-space point from a pointer event, scaled from the element's
// displayed (CSS) size back to the canvas's native pixel size.
function toCanvasPoint(canvas: HTMLCanvasElement, clientX: number, clientY: number): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY
  };
}

function normalizeRect(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y)
  };
}

export default function ScreenshotRedactor({ file, onFlattened, onCancel }: ScreenshotRedactorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The decoded source bitmap. Held only long enough to paint it plus
  // committed redactions onto the canvas on every redraw; never handed to
  // the caller and explicitly dropped (set to null) once flattening happens.
  const imageRef = useRef<HTMLImageElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const draggingRef = useRef<{ start: { x: number; y: number } } | null>(null);

  const [rects, setRects] = useState<Rect[]>([]);
  const [previewRect, setPreviewRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const [flattened, setFlattened] = useState(false);

  // Load the file into an <img>, size the canvas to its natural resolution,
  // and draw the base image once decode completes.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
        }
      }
      setReady(true);
    };
    img.src = url;

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      imageRef.current = null;
    };
    // Mount-only: `file` is the prop identity for this component's whole
    // lifetime (the caller remounts it per-capture), so re-running on other
    // deps would just re-decode the same bitmap.
  }, []);

  // Redraw: base image + every committed rect, painted opaque. This is the
  // only place pixels are touched, so the canvas is always either "exactly
  // the source" (no rects yet) or "source with redactions burned in" — never
  // a state the un-redacted original can be recovered from once a rect is
  // committed over it.
  const redraw = (liveRects: Rect[]) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // DELIBERATE literal, and the one place in this module a raw hex is
    // correct: this is a safety fallback, not styling. The widget mounts
    // app-wide and could render outside .helpdesk-shell, where --redact-fill
    // does not resolve. An unresolved token here would mean redaction boxes
    // that do not reliably paint — a privacy failure, not a cosmetic one.
    // Do not "fix" this to a bare var() read.
    // theme-exempt: canvas fillStyle safety fallback when --redact-fill does not resolve (widget can mount outside .helpdesk-shell); an unpainted redaction box is a privacy failure
    const fill = getComputedStyle(canvas).getPropertyValue('--redact-fill').trim() || '#0A0A0B';

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = fill;
    liveRects.forEach((r) => {
      ctx.fillRect(r.x, r.y, r.w, r.h);
    });
  };

  useEffect(() => {
    if (ready) redraw(rects);
    // redraw is a stable pure function of (rects, the canvas, the still-live
    // image ref); re-running on `ready`/`rects` is exactly the intended
    // trigger set.
  }, [ready, rects]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (flattened) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    const pt = toCanvasPoint(canvas, e.clientX, e.clientY);
    draggingRef.current = { start: pt };
    setPreviewRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pt = toCanvasPoint(canvas, e.clientX, e.clientY);
    setPreviewRect(normalizeRect(draggingRef.current.start, pt));
  };

  const commitDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const pt = toCanvasPoint(canvas, e.clientX, e.clientY);
      const rect = normalizeRect(draggingRef.current.start, pt);
      // Ignore accidental clicks/taps that produce a near-zero rectangle.
      if (rect.w > 3 && rect.h > 3) {
        setRects((prev) => [...prev, rect]);
      }
    }
    draggingRef.current = null;
    setPreviewRect(null);
  };

  const handleUndo = () => {
    setRects((prev) => prev.slice(0, -1));
  };

  const handleDiscard = () => {
    imageRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    onCancel();
  };

  const handleUse = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // The canvas already holds "source + committed redactions" (redraw()
    // above burns every rect in). Export that, then drop every reference to
    // the un-redacted source so nothing but the flattened blob remains
    // reachable from this component.
    canvas.toBlob((blob) => {
      if (blob) {
        onFlattened(blob);
      }
      imageRef.current = null;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setFlattened(true);
    }, 'image/png');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)' }}>
          Drag on the image to redact
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          border: '1px solid var(--hairline-strong)',
          borderRadius: 'var(--r-sm)',
          overflow: 'hidden',
          background: 'var(--paper)',
          lineHeight: 0
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={commitDrag}
          onPointerLeave={commitDrag}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            cursor: flattened ? 'default' : 'crosshair',
            touchAction: 'none'
          }}
        />
        {previewRect ? (
          <Box
            sx={{
              position: 'absolute',
              pointerEvents: 'none',
              left: 0,
              top: 0,
              // Preview box is drawn in DOM overlay coordinates (canvas is
              // 100% width, scaled) — convert back using the live element.
              transform: (() => {
                const canvas = canvasRef.current;
                if (!canvas) return 'none';
                const rect = canvas.getBoundingClientRect();
                const scaleX = rect.width / canvas.width;
                const scaleY = rect.height / canvas.height;
                return `translate(${previewRect.x * scaleX}px, ${previewRect.y * scaleY}px)`;
              })(),
              width: (() => {
                const canvas = canvasRef.current;
                if (!canvas) return 0;
                const rect = canvas.getBoundingClientRect();
                return `${previewRect.w * (rect.width / canvas.width)}px`;
              })(),
              height: (() => {
                const canvas = canvasRef.current;
                if (!canvas) return 0;
                const rect = canvas.getBoundingClientRect();
                return `${previewRect.h * (rect.height / canvas.height)}px`;
              })(),
              background: 'var(--redact-fill)',
              opacity: 0.65,
              borderRadius: '3px'
            }}
          />
        ) : null}
      </Box>

      <Typography sx={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55 }}>
        Redaction is destructive and client-side: rectangles are burned into the picture
        before anything is sent. The un-redacted image is never uploaded and never leaves
        your browser.
      </Typography>

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <HdButton
          startIcon={<UndoIcon sx={{ fontSize: 15 }} />}
          onClick={handleUndo}
          disabled={rects.length === 0 || flattened}
        >
          Undo
        </HdButton>
        <Box sx={{ flex: 1 }} />
        <HdButton onClick={handleDiscard}>
          Discard
        </HdButton>
        <HdButton
          hdVariant="primary"
          startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
          onClick={handleUse}
          disabled={!ready || flattened}
        >
          Use this image
        </HdButton>
      </Box>
    </Box>
  );
}

// ── Auto-detection: explicitly not implemented ─────────────────────────────
// The governance spec's `--redact-auto` / `--redact-auto-bg` dashed
// "candidate" boxes (prototype: `WidgetRedact`'s "2 auto-detected" tag and
// the "NAME?" dashed region) come from scanning the LIVE page DOM for
// `data-pii`-marked elements at capture time. This component only receives a
// `File` — by the time it mounts, the DOM that produced that file is gone —
// so there is no signal here to derive candidate regions from. Wiring that
// up means the CALLER (whatever performs the capture, i.e. FeedbackWidget or
// an eventual html2canvas-style capture step) walking `document.querySelectorAll
// ('[data-pii]')`, computing each element's viewport rect at capture time,
// and passing that list in as a new prop this component would then render as
// dashed overlays. That is out of scope for this file and is not faked here.
